<template>
  <header
    v-if="title"
    class="input-frame-header flex justify-content-between align-items-center"
    :class="{
      container: !containerless,
      'px-2': containerless,
      'px-0': !containerless,
      'input-frame-header--no-action-wrap': !wrapActions,
    }"
  >
    <div class="input-frame-header__content flex align-items-center justify-content-between w-100">
      <div class="input-frame-header__title flex gap-2 justify-content-between align-items-center">
        <div v-if="$slots['header-pre'] != null" class="input-frame-header__pre">
          <slot name="header-pre" />
        </div>
        <h1
          class="input-frame-header__heading"
          :title="title"
          @click="$emit('click:hiddenFeature')"
        >
          {{ title }}
          <span v-if="$slots['header-trailing'] != null" class="input-frame-header__trailing">
            <slot name="header-trailing" />
          </span>
        </h1>
        <div
          v-if="$slots['header-trailing-after'] != null"
          class="input-frame-header__trailing-after"
        >
          <slot name="header-trailing-after" />
        </div>
        <InfoTooltip
          v-if="ENV.VITE_APP_VALIDATION_DEBUG && validation != null"
          title="Dirty fields"
          experimental-design
          max-width="700px"
        >
          <template #activator>
            <button
              type="button"
              class="input-frame-header__dirty-debug"
              aria-label="Show dirty validation fields"
            >
              <i class="fas fa-bug" />
              <span>{{ validation.dirtyFields.length }}</span>
            </button>
          </template>
          <ul v-if="validation.dirtyFields.length > 0" class="m-0 pl-3">
            <li v-for="field in validation.dirtyFields" :key="field.id">
              {{ formatDirtyField(field) }}
            </li>
          </ul>
          <span v-else>No dirty fields</span>
        </InfoTooltip>
      </div>
      <div v-if="$slots['headerCenter'] != null" class="input-frame-header__center">
        <slot name="headerCenter" />
      </div>
      <div
        v-if="$slots['select'] != null || overwriteCreate || createTo != null"
        class="input-frame-header__actions flex justify-content-between align-items-center"
      >
        <div v-if="$slots['select'] != null" class="input-frame-header__select">
          <slot name="select" />
        </div>
        <div
          v-if="overwriteCreate || createTo != null"
          class="input-frame-header__create text-right"
        >
          <LISAButton
            data-testid="inputFrameHeaderCreateButton"
            :disabled="disableCreate === true"
            @click="jumpToCreate"
          >
            <slot name="frame-create">
              <i class="fas fa-plus pr-2" />{{ t("global.create") }}
            </slot>
          </LISAButton>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import LISAButton from "@LISAPrime/LISAButton.vue";
import { type PropType } from "vue";
import { type RouteLocationRaw, useRouter } from "vue-router";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { ENV } from "@/ENV";
import type { AggregatedDirtyField, FormValidationController } from "@/modules/formValidation";

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: null,
  },
  createTo: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    required: false,
    default: null,
  },
  containerless: {
    type: Boolean,
    required: false,
    default: false,
  },
  overwriteCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  wrapActions: {
    type: Boolean,
    required: false,
    default: true,
  },
  validation: {
    type: Object as PropType<FormValidationController>,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits<{
  "click:create": [];
  "click:hiddenFeature": [];
}>();

const router = useRouter();

function jumpToCreate() {
  emit("click:create");
  if (props.createTo != null) {
    router.push(props.createTo);
  }
}

function formatDirtyField(field: AggregatedDirtyField): string {
  const path = field.path.length > 0 ? `${field.path.join(" / ")} / ` : "";
  const label = field.displayName || field.name;
  return `${path}${label}: current=${formatDebugValue(field.value)}, baseline=${formatDebugValue(field.baseline)}`;
}

function formatDebugValue(value: unknown): string {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }
  if (
    value == null ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  try {
    const serialized = JSON.stringify(value);
    return serialized == null ? String(value) : serialized;
  } catch {
    return Object.prototype.toString.call(value);
  }
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.input-frame-header__content,
.input-frame-header__title,
.input-frame-header__heading,
.input-frame-header__center,
.input-frame-header__actions,
.input-frame-header__create,
.input-frame-header__select,
.input-frame-header__pre,
.input-frame-header__trailing-after {
  min-width: 0;
}

.input-frame-header {
  container-type: inline-size;
}

.input-frame-header__content {
  gap: 0.75rem;
  flex-wrap: wrap;
}

.input-frame-header__title {
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  justify-content: flex-start !important;
}

.input-frame-header__pre,
.input-frame-header__trailing-after {
  flex: 0 0 auto;
  white-space: nowrap;
}

.input-frame-header__dirty-debug {
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  color: var(--p-primary-color);
  cursor: pointer;
  background: transparent;
  border: 1px solid var(--p-primary-color);
  border-radius: var(--p-border-radius-sm);
}

.input-frame-header__heading {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.input-frame-header__trailing {
  font-size: 0.6em;
}

.input-frame-header__center {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  max-width: 100%;
}

.input-frame-header__actions {
  flex: 1 1 auto;
  flex-wrap: nowrap;
  gap: 0.5rem;
  justify-content: flex-end !important;
  max-width: 100%;
}

.input-frame-header__create {
  flex: 0 1 auto;
  max-width: 100%;
}

.input-frame-header__select {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
  max-width: 100%;
}

.input-frame-header__center :deep(> *),
.input-frame-header__select :deep(> *) {
  flex: 0 1 auto;
  min-width: 0;
}

.input-frame-header__select :deep(> .flex) {
  max-width: 100%;
}

.input-frame-header--no-action-wrap {
  .input-frame-header__actions {
    flex-wrap: nowrap;
  }

  .input-frame-header__select :deep(.flex) {
    flex-wrap: nowrap;
  }
}

@container (max-width: 40rem) {
  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(.flex) {
    flex-wrap: wrap;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap) {
    align-items: stretch !important;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__content {
    margin-top: 1rem;
    gap: 0.5rem;
    flex-direction: column;
    align-items: stretch !important;
    justify-content: flex-start !important;
    min-height: auto;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__title,
  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__center,
  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__actions,
  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__select {
    flex: 0 1 auto;
    width: 100%;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__actions {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.5rem;
    justify-content: flex-start !important;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__center,
  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__select {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap) .input-frame-header__actions > *,
  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__center
    :deep(> *),
  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> *) {
    flex: 1 1 100%;
    width: 100% !important;
    margin-right: 0 !important;
    margin-left: 0 !important;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> .flex) {
    width: 100%;
    justify-content: flex-start !important;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> .flex > *) {
    flex: 1 1 100%;
    min-width: 0 !important;
    width: 100%;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> .info-tooltip),
  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> .flex > .info-tooltip) {
    display: block !important;
    width: 100%;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> .info-tooltip > :first-child),
  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(> .flex > .info-tooltip > :first-child) {
    display: block !important;
    width: 100%;
  }

  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__actions
    :deep(.p-button),
  .input-frame-header:not(.input-frame-header--no-action-wrap)
    .input-frame-header__select
    :deep(.p-button) {
    width: 100% !important;
  }
}
</style>
